import * as Sentry from '@sentry/react';
import { APP_MODE, ENABLE_MONITORING, SENTRY_DSN } from '../config/env';
import { scrubTelemetry } from '../utils/scrubTelemetry';

let initialized = false;
export const initMonitoring = (config?: { environment?: string; release?: string; debug?: boolean }) => {
  if (initialized || !ENABLE_MONITORING) return;
  if (!SENTRY_DSN || !/^https:\/\//.test(SENTRY_DSN)) {
    console.error('[Monitoring] Missing valid Sentry configuration');
    return;
  }
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
    environment: APP_MODE,
    release: config?.release,
    debug: APP_MODE !== 'production' && config?.debug === true,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console') return null;
      return scrubTelemetry(breadcrumb) as typeof breadcrumb;
    },
    beforeSend(event) {
      if (event.user) event.user = { id: event.user.id };
      if (event.request) {
        delete event.request.headers;
        delete event.request.cookies;
        delete event.request.data;
        delete event.request.query_string;
      }
      return scrubTelemetry(event) as typeof event;
    },
    beforeSendTransaction(event) {
      if (event.user) event.user = { id: event.user.id };
      if (event.request) {
        delete event.request.headers;
        delete event.request.cookies;
        delete event.request.data;
        delete event.request.query_string;
      }
      return scrubTelemetry(event) as typeof event;
    },
  });
  initialized = true;
};

export const captureError = (error: Error | string, context?: {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
  user?: { id: string; role?: string; team?: string };
  component?: string;
  action?: string;
}) => {
  if (!initialized) {
    console.error('[Monitoring] Error captured before monitoring initialization');
    return;
  }
  Sentry.withScope(scope => {
    if (context?.tags) scope.setTags(context.tags);
    if (context?.extra) scope.setExtras(scrubTelemetry(context.extra) as Record<string, unknown>);
    if (context?.user) scope.setUser({ id: context.user.id });
    if (context?.level) scope.setLevel(context.level);
    if (context?.component || context?.action) scope.setContext('ui', { component: context.component, action: context.action });
    if (typeof error === 'string') Sentry.captureMessage(error, context?.level || 'error');
    else Sentry.captureException(error);
  });
};
export const setUser = (user: { id: string; role?: string; team?: string }) => {
  if (initialized) Sentry.setUser({ id: user.id });
};
export const clearUser = () => { if (initialized) Sentry.setUser(null); };
export const ErrorBoundary = Sentry.ErrorBoundary;
export { Sentry };
